import * as React from 'react';
import { connect } from 'react-redux';
import { connectSsr } from '@bart.sk-eshop/ssr-service';
import { __ } from '@bart.sk-eshop/react-i18n';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router';
import styled from 'styled-components';
import { rem, rgba } from 'polished';
import {
  URL_FOR_ORGANIZATIONS,
  URL_REGISTRATION_COMPANY,
  URL_LOGIN,
  URL_BENEFIT_CARD,
} from '../../../constants';
import IconLazy from '../../../components/_helpers/Icon/IconLazy';
import MetaTags from '../../../components/MetaTags/MetaTags';
import {
  Page,
  ContentBox,
  Section,
  PillButton,
  OutlineButton,
  YellowButton,
  BadgeIcon,
  mapFooterState,
  ProductCards,
  ProductCard,
  ProductFolder,
  ProductImg,
  ProductBody,
  ProductTitle,
  ProductList,
  ProductItem,
  ProductItemIcon,
  ProductItemLink,
  ProductButtonRow,
  PromoBanners,
  headingInset,
  audienceColor,
  sectionHeadingBase,
  desktopHeaderButton,
  SITE_ORIGIN,
  StructuredData,
} from '../_shared';

const ASSET = '/images/firmy-intro';
const SCHOOL_ASSET = '/images/school-intro';
const ESHOP = '/kancelaria';

/* ------------------------------------------------------------------ *
 * Section 1 — Hero: office photo + heading + tag pills + two CTAs
 * ------------------------------------------------------------------ */

const Hero = styled(Section)`
  display: flex;
  flex-direction: column;
`;

const HeroCard = styled.div`
  position: relative;
  border-radius: ${rem(16)};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: ${audienceColor('pinkHero')};
  ${({ theme }) => theme.media('md')} {
    border-radius: ${rem(24)};
    min-height: ${rem(460)};
    justify-content: flex-start;
  }
`;

const HeroPhotoMobile = styled.div`
  width: 100%;
  aspect-ratio: 3 / 2;
  background: ${({ theme }) =>
      `linear-gradient(180deg, ${rgba(theme.color.audience.pinkHero, 0)} 78%, ${
        theme.color.audience.pinkHero
      } 100%)`},
    url('${ASSET}/hero-office.webp') center / cover no-repeat;
  ${({ theme }) => theme.media('md')} {
    display: none;
  }
`;

const HeroPhotoDesktop = styled.div`
  display: none;
  ${({ theme }) => theme.media('md')} {
    display: block;
    position: absolute;
    inset: 0;
    background: ${({ theme }) =>
        `linear-gradient(90deg, ${theme.color.audience.pinkHero} 30%, ${rgba(
          theme.color.audience.pinkHero,
          0.7,
        )} 48%, ${rgba(theme.color.audience.pinkHero, 0)} 66%)`},
      url('${ASSET}/hero-office.webp') right center / cover no-repeat;
  }
`;

const HeroPanel = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  padding: ${rem(20)} ${rem(20)} ${rem(24)};
  ${({ theme }) => theme.media('md')} {
    max-width: ${rem(650)};
    gap: ${rem(32)};
    padding: ${rem(60)};
  }
`;

const HeroHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.headings};
  font-weight: 700;
  font-size: ${rem(34)};
  line-height: 1.24;
  color: ${audienceColor('maroon')};
  ${({ theme }) => theme.media('md')} {
    font-size: ${rem(42)};
  }
`;

const HeroSubtitle = styled.p`
  margin: 0;
  font-size: ${rem(18)};
  font-weight: 700;
  line-height: 1.4;
  color: ${audienceColor('red')};
  ${({ theme }) => theme.media('md')} {
    font-size: ${rem(21)};
    max-width: ${rem(360)};
  }
`;

const HeroTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(12)};
  margin-bottom: ${rem(24)};
`;

const HeroTag = styled(Link)`
  display: inline-flex;
  align-items: center;
  /* Shorter pills on mobile (Figma). */
  padding: ${rem(6)} ${rem(16)};
  border-radius: ${rem(999)};
  background: ${({ theme }) => theme.color.white};
  color: ${audienceColor('redCta')};
  font-size: ${rem(15)};
  font-weight: 600;
  line-height: 1.4;
  text-decoration: none;
  &:hover,
  &:focus {
    color: ${audienceColor('redCta')};
    background: ${audienceColor('grayLighter')};
    text-decoration: none;
  }
  ${({ theme }) => theme.media('md')} {
    padding: ${rem(8)} ${rem(20)};
    font-size: ${rem(16)};
    line-height: 1.5;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    gap: ${rem(16)};
  }
`;

const HERO_TAGS = [
  { label: __('Kancelárske potreby'), url: '/kancelaria' },
  { label: __('Reklamné predmety'), url: '/reklamne-predmety' },
  { label: __('Drogéria'), url: '/drogeria-upratovanie' },
  { label: __('Výroba razítok'), url: '/firmy-a-organizacie' },
  { label: __('Kalendáre a diáre'), url: '/potlac-kalendarov-a-diarov' },
];

const HeroSection = () => (
  <Hero>
    <HeroCard>
      <HeroPhotoDesktop aria-hidden="true" />
      <HeroPhotoMobile
        role="img"
        aria-label={__('Všetko pre vašu kanceláriu')}
      />
      <HeroPanel>
        <HeroHead>
          <HeroTitle>{__('Všetko pre vašu kanceláriu')}</HeroTitle>
          <HeroSubtitle>
            {__('za výhodné veľkoobchodné ceny so zľavou až do 40 %')}
          </HeroSubtitle>
        </HeroHead>
        <HeroTags>
          {HERO_TAGS.map((t) => (
            <HeroTag key={t.label} to={t.url}>
              {t.label}
            </HeroTag>
          ))}
        </HeroTags>
        <HeroButtons>
          <PillButton to={URL_REGISTRATION_COMPANY}>
            {__('Registrovať firmu')}
          </PillButton>
          <OutlineButton to={ESHOP}>{__('Prejsť do e-shopu')}</OutlineButton>
        </HeroButtons>
      </HeroPanel>
    </HeroCard>
  </Hero>
);

/* ------------------------------------------------------------------ *
 * Section 2 — Výhody registrácie: two benefit cards (e-shop / predajňa)
 * ------------------------------------------------------------------ */

const Vyhody = styled(Section)`
  display: flex;
  flex-direction: column;
  gap: ${rem(32)};
`;

const SectionHeading = styled.h2`
  ${sectionHeadingBase};
  color: ${audienceColor('dark')};
  ${headingInset}
`;

const BenefitCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    align-items: stretch;
    & > * {
      flex: 1 1 0;
    }
  }
`;

const BenefitCard = styled.div`
  background: ${({ theme }) => theme.color.white};
  border-radius: ${rem(24)};
  box-shadow: 0 ${rem(4)} ${rem(16)} rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const BenefitHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(24)};
  background: ${audienceColor('creamHeader')};
  padding: ${rem(20)} ${rem(24)};
`;

const BenefitHeaderIcon = styled(BadgeIcon)`
  width: ${rem(56)};
  height: ${rem(56)};
  background: ${({ theme }) => theme.color.secondary};
  border-radius: 50%;
`;

const BenefitHeaderTitle = styled.h3`
  margin: 0;
  font-size: ${rem(21)};
  font-weight: 700;
  line-height: 1.38;
  color: ${audienceColor('dark')};
`;

const BenefitBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(32)};
  padding: ${rem(24)};
  ${({ theme }) => theme.media('md')} {
    flex: 1 1 auto;
    padding: ${rem(32)};
  }
`;

const BenefitSubtitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.headings};
  font-size: ${rem(22)};
  font-weight: 700;
  line-height: 1.36;
  color: ${audienceColor('gold')};
`;

const BenefitList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  font-size: ${rem(16)};
  font-weight: 600;
  line-height: 1.5;
  color: ${audienceColor('dark')};
`;

const BenefitCheck = styled.img`
  width: ${rem(24)};
  height: ${rem(24)};
  flex: 0 0 auto;
`;

const BenefitButtonRow = styled.div`
  display: flex;
  /* Column so the button stretches full-width on mobile (Figma). */
  flex-direction: column;
  ${({ theme }) => theme.media('md')} {
    margin-top: auto;
  }
`;

interface BenefitCardData {
  icon: string;
  title: string;
  subtitle: string;
  items: string[];
  cta: string;
  to: string;
}

const BENEFIT_CARDS: BenefitCardData[] = [
  {
    icon: 'ico-benefit-shop.svg',
    title: __('Výhody nákupu v e-shope'),
    subtitle: __('Špeciálne ceny až do -40 %'),
    items: [
      __('Veľkoobchodné ceny'),
      __('Prehľad vytvorených objednávok'),
      __('Doprava zdarma nad 49 €'),
    ],
    cta: __('Registrovať sa'),
    to: URL_REGISTRATION_COMPANY,
  },
  {
    icon: 'ico-benefit-basket.svg',
    title: __('Výhody nákupu v predajni'),
    subtitle: __('Zľava 10 % z cien v predajni'),
    items: [
      __('Zľavy v celej sieti predajní'),
      __('Osobitný prístup'),
      __('Objednanie nedostupného tovaru'),
    ],
    cta: __('Požiadať o kartu výhod'),
    to: URL_BENEFIT_CARD,
  },
];

const VyhodySection = () => (
  <Vyhody>
    <SectionHeading>{__('Výhody registrácie')}</SectionHeading>
    <BenefitCards>
      {BENEFIT_CARDS.map((c) => (
        <BenefitCard key={c.title}>
          <BenefitHeader>
            <BenefitHeaderIcon src={`${ASSET}/${c.icon}`} />
            <BenefitHeaderTitle>{c.title}</BenefitHeaderTitle>
          </BenefitHeader>
          <BenefitBody>
            <BenefitSubtitle>{c.subtitle}</BenefitSubtitle>
            <BenefitList>
              {c.items.map((it) => (
                <BenefitItem key={it}>
                  <BenefitCheck
                    src={`${SCHOOL_ASSET}/skvelko-check.svg`}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                  />
                  {it}
                </BenefitItem>
              ))}
            </BenefitList>
            <BenefitButtonRow>
              <YellowButton to={c.to}>{c.cta}</YellowButton>
            </BenefitButtonRow>
          </BenefitBody>
        </BenefitCard>
      ))}
    </BenefitCards>
  </Vyhody>
);

/* ------------------------------------------------------------------ *
 * Section 3 — Výhodné nakupovanie cez obchodných zástupcov:
 * pink panel with 3 feature icons + 2 sales-rep contact cards
 * ------------------------------------------------------------------ */

const Reps = styled(Section)`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  ${({ theme }) => theme.media('md')} {
    gap: ${rem(32)};
  }
`;

const RepHeading = styled.h2`
  ${sectionHeadingBase};
  color: ${audienceColor('red')};
  ${headingInset}
`;

const RepHeadingAccent = styled.span`
  color: ${audienceColor('maroon')};
`;

const RepPanel = styled.div`
  background: ${audienceColor('pink')};
  border-radius: ${rem(24)};
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  padding: ${rem(24)} ${rem(20)};
  ${({ theme }) => theme.media('md')} {
    border-radius: ${rem(32)};
    gap: ${rem(32)};
    padding: ${rem(40)};
  }
`;

const RepFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    gap: ${rem(24)};
    & > * {
      flex: 1 1 0;
    }
  }
`;

const RepFeature = styled.div`
  display: flex;
  /* Mobile: icon on top, text below, centred (Figma). Desktop: icon left. */
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    text-align: left;
  }
`;

const RepFeatureIcon = styled(BadgeIcon)`
  width: ${rem(45)};
  height: ${rem(45)};
`;

const RepFeatureText = styled.span`
  font-size: ${rem(16)};
  font-weight: 400;
  line-height: 1.4;
  color: ${audienceColor('dark')};
`;

const RepCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    gap: ${rem(24)};
    & > * {
      flex: 1 1 0;
    }
  }
`;

const RepCard = styled.div`
  background: ${({ theme }) => theme.color.white};
  border-radius: ${rem(16)};
  display: flex;
  /* Mobile: photo on top, details below, centred (Figma). Desktop: photo left. */
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${rem(16)};
  padding: ${rem(24)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    align-items: center;
    text-align: left;
    gap: ${rem(20)};
    padding: ${rem(20)} ${rem(24)};
  }
`;

const RepPhoto = styled.img`
  width: ${rem(88)};
  height: ${rem(88)};
  flex: 0 0 auto;
  border-radius: 50%;
  object-fit: cover;
`;

const RepInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(4)};
  ${({ theme }) => theme.media('md')} {
    align-items: flex-start;
  }
`;

const RepName = styled.span`
  font-size: ${rem(21)};
  font-weight: 700;
  line-height: 1.38;
  color: ${audienceColor('red')};
`;

const RepPosition = styled.span`
  font-size: ${rem(16)};
  font-weight: 700;
  line-height: 1.5;
  color: ${audienceColor('maroon')};
  margin-bottom: ${rem(16)};
`;

const RepContacts = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
  /* Desktop: phone + mail sit on a single row (Figma). */
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    align-items: center;
    gap: ${rem(24)};
  }
`;

const RepContact = styled.a`
  display: flex;
  align-items: center;
  gap: ${rem(10)};
  font-size: ${rem(15)};
  line-height: 1.5;
  color: ${audienceColor('dark')};
  text-decoration: underline;

  &:hover {
    color: ${audienceColor('dark')};
    text-decoration: underline;
  }
`;

const RepContactBadge = styled.span`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(32)};
  height: ${rem(32)};
  border-radius: 50%;
  background: ${({ theme }) => rgba(theme.color.audience.red, 0.1)};
`;

const RepContactIcon = styled(IconLazy)`
  width: ${rem(16)};
  height: ${rem(16)};
  fill: ${({ theme }) => theme.color.primary};
`;

interface RepFeatureData {
  icon: string;
  text: string;
}

const REP_FEATURES: RepFeatureData[] = [
  { icon: 'ico-rep-chair.svg', text: __('Individuálne množstevné zľavy') },
  {
    icon: 'ico-rep-shield.svg',
    text: __('Možnosť urýchleného doručenia tovaru do 24 hodín'),
  },
  {
    icon: 'ico-rep-slovakia.svg',
    text: __(
      'Možnosť výberu aj z tovaru, ktorý nie je dostupný v eshope a v maloobchodnej sieti predajní',
    ),
  },
];

interface RepData {
  name: string;
  position: string;
  phone: string;
  mail: string;
  photo: string;
}

const REPS: RepData[] = [
  {
    name: __('Zuzana Vršková'),
    position: __('externý obchodný zástupca'),
    phone: '0903 284 799',
    mail: 'vrskova@daffer.sk',
    photo: '/images/contact/vrskova.png',
  },
  {
    name: __('Mgr. Martin Pružinec'),
    position: __('obchodný zástupca'),
    phone: '0903 284 873',
    mail: 'pruzinec@daffer.sk',
    photo: '/images/contact/pruzinec.png',
  },
];

const RepsSection = () => (
  <Reps>
    <RepHeading>
      {__('Výhodné nakupovanie')}
      <br />
      <RepHeadingAccent>{__('cez obchodných zástupcov')}</RepHeadingAccent>
    </RepHeading>
    <RepPanel>
      <RepFeatures>
        {REP_FEATURES.map((f) => (
          <RepFeature key={f.icon}>
            <RepFeatureIcon src={`${ASSET}/${f.icon}`} />
            <RepFeatureText>{f.text}</RepFeatureText>
          </RepFeature>
        ))}
      </RepFeatures>
      <RepCards>
        {REPS.map((r, i) => (
          <RepCard key={i}>
            <RepPhoto
              src={r.photo}
              width={120}
              height={120}
              alt={r.name}
              loading="lazy"
              decoding="async"
            />
            <RepInfo>
              <RepName>{r.name}</RepName>
              <RepPosition>{r.position}</RepPosition>
              <RepContacts>
                <RepContact href={`tel:${r.phone.replace(/\s/g, '')}`}>
                  <RepContactBadge>
                    <RepContactIcon icon="phone" />
                  </RepContactBadge>
                  {r.phone}
                </RepContact>
                <RepContact href={`mailto:${r.mail}`}>
                  <RepContactBadge>
                    <RepContactIcon icon="mail" />
                  </RepContactBadge>
                  {r.mail}
                </RepContact>
              </RepContacts>
            </RepInfo>
          </RepCard>
        ))}
      </RepCards>
    </RepPanel>
  </Reps>
);

/* ------------------------------------------------------------------ *
 * Section 4 — Ponuka produktov a služieb: 3 "folder" category cards
 * ------------------------------------------------------------------ */

const Ponuka = styled.section`
  background: ${audienceColor('bgGray')};
  /* Only the top corners are rounded (Figma) — the bottom stays square. */
  border-radius: ${rem(24)} ${rem(24)} 0 0;
  /* Extra bottom padding gives the connected Registration section room to tuck
     up into the grey block (kept on both breakpoints so it isn't wiped by the
     desktop padding shorthand). */
  padding: ${rem(32)} ${rem(16)} ${rem(80)};
  display: flex;
  flex-direction: column;
  /* Tighter on mobile so the bottom "show all" button isn't far from the cards. */
  gap: ${rem(32)};
  ${({ theme }) => theme.media('md')} {
    gap: ${rem(72)};
    border-radius: ${rem(32)} ${rem(32)} 0 0;
    padding: ${rem(56)} ${rem(56)} ${rem(80)};
  }
`;

const PonukaHeader = styled.div`
  ${headingInset}
  /* The inset lives on the row, so the inner heading must not add it again. */
  ${SectionHeading} {
    margin-left: 0;
    margin-right: 0;
  }
  ${({ theme }) => theme.media('md')} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${rem(24)};
  }
`;

const PonukaHeaderButton = styled(YellowButton)`
  ${desktopHeaderButton};
`;

const PonukaBottomButton = styled(YellowButton)`
  align-self: stretch;
  ${({ theme }) => theme.media('md')} {
    display: none;
  }
`;

interface ProductPart {
  label: string;
  url: string;
}

interface ProductData {
  img: string;
  title: string;
  url: string;
  items: { icon: string; parts: ProductPart[] }[];
}

const PRODUCT_CARDS: ProductData[] = [
  {
    img: 'prod-kancelarske.webp',
    title: __('Kancelárske potreby'),
    url: '/kancelaria',
    items: [
      {
        icon: 'ico-file.svg',
        parts: [
          { label: __('Tlačivá'), url: '/tlaciva' },
          { label: __('pečiatky'), url: '/peciatky-datumovky' },
          { label: __('papiere'), url: '/papier-do-kancelarie' },
        ],
      },
      {
        icon: 'ico-mail.svg',
        parts: [
          { label: __('Obálky'), url: '/obalky' },
          { label: __('zakladače'), url: '/archivacia-a-zakladanie' },
          { label: __('organizéry'), url: '/archivacia-a-zakladanie' },
        ],
      },
      {
        icon: 'ico-pencil.svg',
        parts: [
          { label: __('Písacie potreby'), url: '/pisanie-a-korekcia' },
          { label: __('lepidlá'), url: '/lepidla-a-lepiace-pasky' },
          { label: __('pásky'), url: '/lepidla-a-lepiace-pasky' },
        ],
      },
      {
        icon: 'ico-bottle.svg',
        parts: [
          {
            label: __('Kancelárska technika'),
            url: '/kancelarska-technika-media',
          },
        ],
      },
    ],
  },
  {
    img: 'prod-reklamne.webp',
    title: __('Reklamné predmety'),
    url: '/reklamne-predmety',
    items: [
      {
        icon: 'ico-printer.svg',
        parts: [
          { label: __('Potlač, gravírovanie'), url: '/reklamne-predmety' },
        ],
      },
      {
        icon: 'ico-gift.svg',
        parts: [
          { label: __('Darčekové perá'), url: '/darcekove-pera' },
          { label: __('firemné darčeky'), url: '/reklamne-predmety' },
        ],
      },
      {
        icon: 'ico-calendar.svg',
        parts: [
          {
            label: __('Potlač kalendárov, diárov a propagačných materiálov'),
            url: '/potlac-kalendarov-a-diarov',
          },
        ],
      },
    ],
  },
  {
    img: 'prod-archivacia.webp',
    title: __('Archivácia a zakladanie'),
    url: '/archivacia-a-zakladanie',
    items: [
      {
        icon: 'ico-folder-check.svg',
        parts: [
          { label: __('Poradače'), url: '/archivacia-a-zakladanie/poradace' },
        ],
      },
      {
        icon: 'ico-briefcase.svg',
        parts: [
          {
            label: __('Obaly na dokumenty'),
            url: '/archivacia-a-zakladanie/obaly-na-dokumenty',
          },
        ],
      },
      {
        icon: 'ico-box.svg',
        parts: [
          {
            label: __('Zásuvkové boxy'),
            url: '/archivacia-a-zakladanie/zasuvkove-boxy',
          },
        ],
      },
      {
        icon: 'ico-package.svg',
        parts: [
          {
            label: __('Kartotéky'),
            url: '/archivacia-a-zakladanie/kartoteky',
          },
        ],
      },
    ],
  },
];

const PonukaSection = () => (
  <Ponuka>
    <PonukaHeader>
      <SectionHeading>{__('Ponuka produktov a služieb')}</SectionHeading>
      <PonukaHeaderButton to={ESHOP}>
        {__('Zobraziť celú ponuku')}
      </PonukaHeaderButton>
    </PonukaHeader>
    <ProductCards>
      {PRODUCT_CARDS.map((c) => (
        <ProductCard key={c.title}>
          <ProductFolder>
            <ProductImg
              src={`${ASSET}/${c.img}`}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
            />
          </ProductFolder>
          <ProductBody>
            <ProductTitle>{c.title}</ProductTitle>
            <ProductList>
              {c.items.map((it, i) => (
                <ProductItem key={i}>
                  <ProductItemIcon
                    src={`${ASSET}/${it.icon}`}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                  />
                  <span>
                    {it.parts.map((p, pi) => (
                      <React.Fragment key={pi}>
                        {pi > 0 && ', '}
                        <ProductItemLink to={p.url}>{p.label}</ProductItemLink>
                      </React.Fragment>
                    ))}
                  </span>
                </ProductItem>
              ))}
            </ProductList>
            <ProductButtonRow>
              <YellowButton to={c.url}>{__('Zobraziť ponuku')}</YellowButton>
            </ProductButtonRow>
          </ProductBody>
        </ProductCard>
      ))}
    </ProductCards>
    <PonukaBottomButton to={ESHOP}>
      {__('Zobraziť celú ponuku')}
    </PonukaBottomButton>
  </Ponuka>
);

/* ------------------------------------------------------------------ *
 * Section 5 — Registrácia: benefits list + two CTAs + photo with Zľavy badge
 * ------------------------------------------------------------------ */

const Registration = styled(Section)`
  margin-top: -70px;
  background: ${({ theme }) => theme.color.white};
  border-radius: 2rem 2rem 0 0;
  /* Full padding shorthand (not just padding-top) so Section's responsive
     padding can't wipe the top inset that compensates the negative margin.
     Horizontal values match Section (16 / 56); bottom 0. */
  padding: 65px ${rem(16)} 0;
  ${({ theme }) => theme.media('md')} {
    padding: 65px ${rem(56)} 0;
  }
`;

const RegCard = styled.div`
  background: ${audienceColor('pink')};
  border-radius: ${rem(24)};
  overflow: hidden;
  display: flex;
  /* Mobile: image first, then content (Figma). Desktop: content left, image right. */
  flex-direction: column-reverse;
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    align-items: stretch;
    border-radius: ${rem(32)};
  }
`;

const RegContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(32)};
  padding: ${rem(32)} ${rem(20)};
  ${({ theme }) => theme.media('md')} {
    /* Exactly half the card. */
    flex: 0 0 50%;
    max-width: 50%;
    min-width: 0;
    gap: ${rem(40)};
    padding: ${rem(60)} ${rem(56)};
  }
`;

const RegHeading = styled.h2`
  ${sectionHeadingBase};
  color: ${audienceColor('red')};
`;

const RegHeadingAccent = styled.span`
  color: ${audienceColor('maroon')};
`;

const RegList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const RegItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  padding: ${rem(16)} 0;
  font-size: ${rem(16)};
  font-weight: 400;
  line-height: 1.5;
  color: ${audienceColor('dark')};

  /* The first benefit wraps within 350px instead of stretching full width. */
  &:first-child {
    max-width: ${rem(350)};
  }

  & + & {
    border-top: ${rem(1)} solid rgba(0, 0, 0, 0.1);
  }

  b {
    font-weight: 700;
  }
`;

const RegCheck = styled.img`
  width: ${rem(24)};
  height: ${rem(24)};
  flex: 0 0 auto;
`;

const RegButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    gap: ${rem(16)};
  }
`;

const RegImageCol = styled.div`
  display: flex;
  ${({ theme }) => theme.media('md')} {
    /* Exactly half the card (50/50 with the content). */
    flex: 0 0 50%;
    max-width: 50%;
    min-width: 0;
    align-items: center;
  }
`;

const RegImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  ${({ theme }) => theme.media('md')} {
    /* Rounded on the left (inner) side; the right side meets the card edge. */
    border-radius: ${rem(32)} 0 0 ${rem(32)};
  }
`;

const RegistrationSection = () => (
  <Registration>
    <RegCard>
      <RegContent>
        <RegHeading>
          {__('Registrácia - jeden klik')}
          <br />
          <RegHeadingAccent>{__('k výhodným ponukám')}</RegHeadingAccent>
        </RegHeading>
        <RegList>
          <RegItem>
            <RegCheck
              src={`${SCHOOL_ASSET}/reg-check.svg`}
              alt=""
              aria-hidden="true"
            />
            <span>
              {__('Výhodné veľkoobchodné ceny so zľavou až do ')}
              <b>{__('40 % okamžite po registrácií')}</b>
            </span>
          </RegItem>
          <RegItem>
            <RegCheck
              src={`${SCHOOL_ASSET}/reg-check.svg`}
              alt=""
              aria-hidden="true"
            />
            {__('Prehľad všetkých objednávok na jednom mieste')}
          </RegItem>
          <RegItem>
            <RegCheck
              src={`${SCHOOL_ASSET}/reg-check.svg`}
              alt=""
              aria-hidden="true"
            />
            {__('Osobný obchodný zástupca pre vašu firmu')}
          </RegItem>
        </RegList>
        <RegButtons>
          <PillButton to={URL_REGISTRATION_COMPANY}>
            {__('Registrovať sa')}
          </PillButton>
          <OutlineButton to={URL_LOGIN}>{__('Prihlásiť sa')}</OutlineButton>
        </RegButtons>
      </RegContent>
      <RegImageCol>
        <RegImage
          src={`${ASSET}/registration.webp`}
          width={1320}
          height={1064}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
        />
      </RegImageCol>
    </RegCard>
  </Registration>
);

/* ------------------------------------------------------------------ *
 * Section 6 — Promo banners: Doprava zdarma + Sieť papiernictiev
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */

interface OrganizationsIntroProps {
  footerPhone?: string;
  footerMail?: string;
}

const OrganizationsIntroBase = ({
  footerPhone,
  footerMail,
}: OrganizationsIntroProps) => (
  <Page>
    <MetaTags
      tags={{
        title: __('Pre firmy a organizácie | Daffer.sk'),
        description: __(
          'Kancelárske potreby, reklamné predmety a drogéria pre firmy a organizácie za veľkoobchodné ceny so zľavou až do 40 %. Doprava zdarma pri nákupe nad 49 €.',
        ),
        og_title: __('Pre firmy a organizácie | Daffer.sk'),
        og_description: __(
          'Kancelárske potreby, reklamné predmety a drogéria pre firmy a organizácie za veľkoobchodné ceny so zľavou až do 40 %. Doprava zdarma pri nákupe nad 49 €.',
        ),
        og_url: `${SITE_ORIGIN}${URL_FOR_ORGANIZATIONS}`,
        og_image: `${SITE_ORIGIN}/images/firmy-intro/hero-office.png`,
        og_image_width: '2121',
        og_image_height: '1414',
      }}
    />
    {/* Preload the LCP hero background (CSS backgrounds are discovered late)
        + the canonical URL (nothing else in the app renders one). */}
    <Helmet>
      <link rel="canonical" href={`${SITE_ORIGIN}${URL_FOR_ORGANIZATIONS}`} />
      <link
        rel="preload"
        as="image"
        href={`${ASSET}/hero-office.webp`}
        {...({ fetchpriority: 'high' } as any)}
      />
    </Helmet>
    <StructuredData />
    <ContentBox>
      <HeroSection />
      <VyhodySection />
      <RepsSection />
      <PonukaSection />
      <RegistrationSection />
      <PromoBanners />
    </ContentBox>
  </Page>
);

const mapStateToProps = (state: any) => {
  const footer = mapFooterState(state);
  return { footerPhone: footer.phone, footerMail: footer.mail };
};

export const OrganizationsIntro = connect(mapStateToProps)(
  connectSsr({ displayName: 'OrganizationsIntro' })(OrganizationsIntroBase),
);

export default OrganizationsIntro;
